import { memo } from 'react'
import type { CatalogFacets } from '../../lib/api.js'

interface CatalogSummaryBarProps {
  facets: CatalogFacets | null
  fallbackFacets: CatalogFacets
  displayedCount: number
}

/**
 * Bande de synthèse de la maquette Pen.
 *
 * Les quatre chiffres viennent des mêmes facettes que la colonne de gauche.
 * Si leur requête échoue, des compteurs explicitement limités aux fiches déjà
 * chargées remplacent les tirets indéfinis.
 */
export const CatalogSummaryBar = memo(function CatalogSummaryBar({
  facets, fallbackFacets, displayedCount,
}: CatalogSummaryBarProps) {
  const resolved = facets ?? fallbackFacets
  const partial = !facets
  const total = resolved.state.all
  const linked = resolved.media.available
  const antennaAlerts = resolved.media.none + resolved.state.missingFile
  const scopeDetail = partial ? `sur ${displayedCount} chargés` : 'sur tout le catalogue'

  return (
    <section className="catalog-summary" aria-label="Synthèse du catalogue">
      <SummaryMetric
        value={String(total)}
        label="programmes"
        detail={`${displayedCount} chargés`}
        tone="brand"
      />
      <SummaryMetric
        value={String(linked)}
        label="médias disponibles"
        detail={scopeDetail}
        tone="success"
      />
      <SummaryMetric
        value={String(resolved.state.incomplete)}
        label="fiches à compléter"
        detail={partial ? scopeDetail : 'titre, genre ou média'}
        tone="warning"
      />
      <SummaryMetric
        value={String(antennaAlerts)}
        label="alertes média"
        detail={partial ? scopeDetail : 'absent ou introuvable'}
        tone="danger"
      />
    </section>
  )
})

const SummaryMetric = memo(function SummaryMetric({
  value, label, detail, tone,
}: {
  value: string
  label: string
  detail: string
  tone: 'brand' | 'success' | 'warning' | 'danger'
}) {
  return (
    <div className="catalog-summary__metric">
      <span className={`catalog-summary__value is-${tone}`}>{value}</span>
      <span className="catalog-summary__copy">
        <span className="catalog-summary__label">{label}</span>
        <span className="catalog-summary__detail">{detail}</span>
      </span>
    </div>
  )
})
